import { ChangeEvent, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useIntl } from 'react-intl';
import { useImportWorkoutAI, ImportWorkoutAIResult } from 'hooks/coach/useImportWorkoutAI';

interface Props {
  visible: boolean;
  onHide: () => void;
  onImported: (result: ImportWorkoutAIResult) => void;
}

const ACCEPTED_MIME_TYPES = '.jpg,.jpeg,.png,.webp,.xlsx,.csv,image/*';
const MAX_FILE_SIZE_MB = 10;

export default function ImportWorkoutAIDialog({ visible, onHide, onImported }: Props) {
  const intl = useIntl();
  const { loading, parseFile, parseText } = useImportWorkoutAI();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [text, setText] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setText('');
    setSelectedFile(null);
    setActiveTab(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleHide = () => {
    if (loading) return; // bloqueamos cerrar mientras parsea
    reset();
    onHide();
  };

  const handleFilePicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleParseFile = async () => {
    if (!selectedFile) return;
    const result = await parseFile(selectedFile);
    if (result) {
      onImported(result);
      reset();
      onHide();
    }
  };

  const handleParseText = async () => {
    const result = await parseText(text);
    if (result) {
      onImported(result);
      reset();
      onHide();
    }
  };

  return (
    <Dialog
      header={intl.formatMessage({ id: 'coach.importWorkoutAI.title' })}
      visible={visible}
      onHide={handleHide}
      draggable={false}
      resizable={false}
      dismissableMask={!loading}
      closable={!loading}
      style={{ width: '42rem', maxWidth: '94vw' }}
    >
      <div className="flex flex-column gap-3">
        <p className="m-0" style={{ color: 'var(--ios-text-secondary)' }}>
          {intl.formatMessage({ id: 'coach.importWorkoutAI.intro' })}
        </p>

        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel header={intl.formatMessage({ id: 'coach.importWorkoutAI.tab.file' })}>
            <div className="flex flex-column gap-3">
              <div
                style={{
                  background: 'var(--ios-surface-subtle)',
                  border: '1px dashed var(--ios-card-border)',
                  borderRadius: 'var(--ios-radius-md)',
                  padding: '1.5rem',
                  textAlign: 'center'
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_MIME_TYPES}
                  onChange={handleFilePicked}
                  style={{ display: 'none' }}
                />
                {selectedFile ? (
                  <div className="flex flex-column align-items-center gap-2">
                    <i className="pi pi-file" style={{ fontSize: '1.5rem', color: '#6366f1' }} />
                    <div style={{ fontWeight: 600 }}>{selectedFile.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--ios-text-secondary)' }}>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </div>
                    <Button
                      label={intl.formatMessage({ id: 'coach.importWorkoutAI.changeFile' })}
                      icon="pi pi-refresh"
                      className="p-button-text p-button-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                    />
                  </div>
                ) : (
                  <div className="flex flex-column align-items-center gap-2">
                    <i
                      className="pi pi-cloud-upload"
                      style={{ fontSize: '2rem', color: 'var(--ios-text-secondary)' }}
                    />
                    <div style={{ fontWeight: 600 }}>
                      {intl.formatMessage({ id: 'coach.importWorkoutAI.dropHint' })}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--ios-text-secondary)' }}>
                      {intl.formatMessage({ id: 'coach.importWorkoutAI.formats' })}
                    </div>
                    <Button
                      label={intl.formatMessage({ id: 'coach.importWorkoutAI.chooseFile' })}
                      icon="pi pi-upload"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-content-end gap-2">
                <Button
                  label={intl.formatMessage({ id: 'common.cancel' })}
                  className="p-button-text"
                  onClick={handleHide}
                  disabled={loading}
                />
                <Button
                  label={intl.formatMessage({ id: 'coach.importWorkoutAI.parseAction' })}
                  icon={loading ? undefined : 'pi pi-sparkles'}
                  onClick={handleParseFile}
                  disabled={!selectedFile || loading}
                  loading={loading}
                />
              </div>
            </div>
          </TabPanel>

          <TabPanel header={intl.formatMessage({ id: 'coach.importWorkoutAI.tab.text' })}>
            <div className="flex flex-column gap-3">
              <InputTextarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                autoResize
                placeholder={intl.formatMessage({ id: 'coach.importWorkoutAI.textPlaceholder' })}
                disabled={loading}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem' }}
              />
              <div className="flex justify-content-end gap-2">
                <Button
                  label={intl.formatMessage({ id: 'common.cancel' })}
                  className="p-button-text"
                  onClick={handleHide}
                  disabled={loading}
                />
                <Button
                  label={intl.formatMessage({ id: 'coach.importWorkoutAI.parseAction' })}
                  icon={loading ? undefined : 'pi pi-sparkles'}
                  onClick={handleParseText}
                  disabled={text.trim().length === 0 || loading}
                  loading={loading}
                />
              </div>
            </div>
          </TabPanel>
        </TabView>

        {loading && (
          <div
            className="flex align-items-center justify-content-center gap-2"
            style={{ color: 'var(--ios-text-secondary)' }}
          >
            <ProgressSpinner style={{ width: '1.25rem', height: '1.25rem' }} strokeWidth="3" />
            <span style={{ fontSize: '0.9rem' }}>{intl.formatMessage({ id: 'coach.importWorkoutAI.parsing' })}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}
